import React, { useState, useEffect } from 'react';
import { TimesheetStatus, ProjectType } from '../types/timesheet.types';
import type { ISODateString, PeriodMonth, BankHoliday } from '../types/timesheet.types';
import type { SuperApprovalItem, WeekSummary } from '../types/approval.types';
import type { UseSuperApprovalsResult } from '../hooks/useSuperApprovals';
import { useIncompleteTimesheets } from '../hooks/useIncompleteTimesheets';
import { useChaseNotifications } from '../hooks/useChaseNotifications';
import { IncompleteTimesheetsSection } from './IncompleteTimesheetsSection';
import { getWeekDays } from '../utils/approvalTransform';
import { fmtHours } from '../utils/calculations';
import { useConnector } from '../contexts/ConnectorContext';
import { BANK_HOLIDAY_PROJECT_CODE, VALIDATION } from '../utils/constants';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitials(name: string): string {
  return name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();
}

function formatPeriodMonthLabel(pm: PeriodMonth): string {
  const [year, month] = pm.split('-');
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];
  return `${months[parseInt(month, 10) - 1]} ${year}`;
}

function formatDate(iso: ISODateString): string {
  const d = new Date(iso + 'T00:00:00Z');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function formatShortDate(iso: ISODateString): string {
  const d = new Date(iso + 'T00:00:00Z');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]}`;
}

function weekColor(status: TimesheetStatus): string {
  switch (status) {
    case TimesheetStatus.SuperApproved:
    case TimesheetStatus.ManagerApproved: return 'var(--green, #22c55e)';
    case TimesheetStatus.Submitted:       return 'var(--accent)';
    case TimesheetStatus.Rejected:        return 'var(--red, #ef4444)';
    default:                              return 'var(--text-dim)';
  }
}

function weekBg(status: TimesheetStatus): string {
  switch (status) {
    case TimesheetStatus.SuperApproved:
    case TimesheetStatus.ManagerApproved: return 'rgba(34,197,94,0.12)';
    case TimesheetStatus.Submitted:       return 'rgba(99,102,241,0.1)';
    case TimesheetStatus.Rejected:        return 'rgba(239,68,68,0.1)';
    default:                              return 'var(--surface2)';
  }
}

function statusBadgeClass(status: TimesheetStatus): string {
  switch (status) {
    case TimesheetStatus.SuperApproved:    return 'super-approved';
    case TimesheetStatus.ManagerApproved:  return 'approved';
    case TimesheetStatus.Rejected:         return 'rejected';
    default:                               return 'pending';
  }
}

function statusBadgeLabel(status: TimesheetStatus): string {
  switch (status) {
    case TimesheetStatus.SuperApproved:   return 'Final Approved';
    case TimesheetStatus.ManagerApproved: return 'Mgr Approved';
    case TimesheetStatus.Rejected:        return 'Rejected';
    default:                              return status;
  }
}

// ─── SVG Icons ────────────────────────────────────────────────────────────────

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2.5 }}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2.5 }}>
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2.5 }}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2.5 }}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function UnlockIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2.5 }}>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 9.9-1" />
    </svg>
  );
}

// ─── WeekChip ─────────────────────────────────────────────────────────────────

function WeekChip({ week, onClick }: { week: WeekSummary; onClick: () => void }) {
  const color = weekColor(week.status);
  const bg = weekBg(week.status);
  return (
    <div
      onClick={onClick}
      title="Click to view week detail"
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      style={{
        flex: 1, minWidth: 110,
        border: '1px solid var(--border)', borderRadius: 8,
        padding: '10px 12px', background: bg, cursor: 'pointer',
        transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.1s',
      }}
      onMouseOver={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.12)';
        el.style.borderColor = 'var(--accent)';
        el.style.transform = 'translateY(-1px)';
      }}
      onMouseOut={e => {
        const el = e.currentTarget as HTMLDivElement;
        el.style.boxShadow = '';
        el.style.borderColor = '';
        el.style.transform = '';
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {week.weekNumber}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{week.label}</div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', marginTop: 6 }}>
        {fmtHours(week.totalHours + week.bhHours)}
      </div>
      <div style={{ fontSize: 10.5, color, marginTop: 2, textTransform: 'capitalize' }}>
        {statusBadgeLabel(week.status).toLowerCase()}
      </div>
      <div style={{ fontSize: 10, color: 'var(--accent)', marginTop: 6, fontWeight: 600, letterSpacing: '0.03em' }}>
        View →
      </div>
    </div>
  );
}

// ─── SuperApprovalCard ────────────────────────────────────────────────────────

function SuperApprovalCard({
  item,
  isActioning,
  softLaunchMode,
  onApprove,
  onReject,
  onUnlock,
  onDrillDown,
}: {
  item: SuperApprovalItem;
  isActioning: boolean;
  softLaunchMode?: boolean;
  onApprove: () => void;
  onReject: () => void;
  onUnlock: () => void;
  onDrillDown: (week: WeekSummary) => void;
}) {
  const isActionable = item.status === TimesheetStatus.ManagerApproved
    || (!!softLaunchMode && item.status === TimesheetStatus.Submitted);
  const isSuperApproved = item.status === TimesheetStatus.SuperApproved;
  const monthLabel = formatPeriodMonthLabel(item.periodMonth);

  const rdHours    = item.breakdownByType[ProjectType.RD]    ?? 0;
  const gaHours    = item.breakdownByType[ProjectType.GA]    ?? 0;
  const leaveHours = item.breakdownByType[ProjectType.Leave] ?? 0;
  const rdPct = item.totalHours > 0 ? Math.round(rdHours / item.totalHours * 100) : 0;
  const totalWithBH = item.weeks.reduce((s, w) => s + w.totalHours + w.bhHours, 0);

  return (
    <div className="ts-card" style={{ marginBottom: 18, opacity: isActionable ? 1 : 0.7 }}>
      {/* Header */}
      <div className="ts-card-hdr">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="av" style={{ width: 28, height: 28, fontSize: 11, flexShrink: 0 }}>
            {getInitials(item.personName)}
          </div>
          <div>
            <div className="ts-card-title">
              {item.personName}
              {item.source === 'asset' && (
                <span
                  className="badge"
                  style={{
                    marginLeft: 8,
                    background: 'rgba(99,102,241,0.15)',
                    color: 'var(--accent)',
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                  }}
                >
                  Asset · {item.assetCode || '—'}
                </span>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
              {item.department}&nbsp;·&nbsp;{monthLabel}
            </div>
          </div>
        </div>
        <span className={`app-badge ${statusBadgeClass(item.status)}`}>
          {statusBadgeLabel(item.status)}
        </span>
      </div>

      {/* Manager approval banner — always shown for items in this view */}
      {item.managerApprovedDate && (
        <div className="info-box green" style={{ margin: '12px 18px 0' }}>
          <CheckIcon />
          &nbsp;Manager approved by&nbsp;<strong>{item.managerName || 'Line Manager'}</strong>&nbsp;on&nbsp;{formatDate(item.managerApprovedDate)}
        </div>
      )}

      {/* Super-approved banner */}
      {isSuperApproved && item.superApprovedDate && (
        <div className="info-box green" style={{ margin: '8px 18px 0' }}>
          <LockIcon />
          &nbsp;Final approved by&nbsp;<strong>{item.superApproverName || 'Super Approver'}</strong>&nbsp;on&nbsp;{formatDate(item.superApprovedDate)}&nbsp;— locked
        </div>
      )}

      {/* Rejected banner */}
      {item.status === TimesheetStatus.Rejected && (
        <div className="info-box red" style={{ margin: '8px 18px 0' }}>
          <XIcon />
          &nbsp;Month rejected — employee has been notified
        </div>
      )}

      {/* Weeks + breakdown */}
      <div style={{ padding: '14px 18px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {item.weeks.map(week => (
            <WeekChip key={week.weekNumber} week={week} onClick={() => onDrillDown(week)} />
          ))}
        </div>

        <div
          style={{
            display: 'flex', gap: 16, flexWrap: 'wrap',
            marginTop: 14, paddingTop: 14,
            borderTop: '1px solid var(--border)',
          }}
        >
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Projects&nbsp;
            <strong style={{ color: 'var(--text)' }}>{fmtHours(rdHours + gaHours)}</strong>
            &nbsp;<span style={{ color: 'var(--accent)' }}>({rdPct}% R&amp;D)</span>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Leave&nbsp;<strong style={{ color: 'var(--text)' }}>{fmtHours(leaveHours)}</strong>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginLeft: 'auto' }}>
            Total&nbsp;<strong style={{ color: 'var(--text)' }}>{fmtHours(totalWithBH)}</strong>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="ts-actions">
        <div className="ts-actions-l">
          <span className="saved-lbl">{monthLabel}</span>
        </div>
        <div className="ts-actions-r">
          {isActionable ? (
            <>
              <button className="btn btn-danger" onClick={onReject} disabled={isActioning}>
                <XIcon />&nbsp;Reject
              </button>
              <button className="btn btn-success" onClick={onApprove} disabled={isActioning}>
                <StarIcon />&nbsp;Final Approve
              </button>
            </>
          ) : isSuperApproved ? (
            <>
              <span className="saved-lbl"><LockIcon />&nbsp;Final Approved — Locked</span>
              <button className="btn btn-ghost btn-sm" onClick={onUnlock} disabled={isActioning}>
                <UnlockIcon />&nbsp;Unlock
              </button>
            </>
          ) : (
            <span className="saved-lbl">Month Rejected</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── WeekDrillDownModal ───────────────────────────────────────────────────────

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function WeekDrillDownModal({
  item,
  week,
  onClose,
}: {
  item: SuperApprovalItem;
  week: WeekSummary;
  onClose: () => void;
}) {
  const connector = useConnector();
  const days = getWeekDays(week.weekStart);

  // ── Bank holiday indication ──────────────────────────────────────────────────
  const [bankHolidays, setBankHolidays] = useState<BankHoliday[]>([]);
  useEffect(() => {
    const year = parseInt(week.weekStart.slice(0, 4), 10);
    void connector.getBankHolidays(year).then(setBankHolidays);
  }, [connector, week.weekStart]);

  // Deduplicate by date — SP can have UK + All rows for the same calendar date
  const bhInWeekMap = new Map<ISODateString, BankHoliday>();
  for (const bh of bankHolidays) {
    if (!bhInWeekMap.has(bh.holidayDate)) bhInWeekMap.set(bh.holidayDate, bh);
  }
  // BH days that fall within this specific week
  const bhDaysInWeek = days.filter(d => bhInWeekMap.has(d));
  // Days where the person entered project hours on a BH (chose to work)
  const daysWithProjectHours = new Set(
    week.entries
      .filter(e => e.projectCode !== BANK_HOLIDAY_PROJECT_CODE && e.hours > 0)
      .map(e => e.dateWorked)
  );

  // Exclude the NON-WORKING sentinel from the project grid (defensive)
  const projectCodes = [...new Set(
    week.entries
      .filter(e => e.projectCode !== BANK_HOLIDAY_PROJECT_CODE)
      .map(e => e.projectCode)
  )];

  const hoursGrid = projectCodes.map(code => ({
    projectCode: code,
    dayHours: days.map(day =>
      week.entries
        .filter(e => e.projectCode === code && e.dateWorked === day)
        .reduce((s, e) => s + e.hours, 0)
    ),
    rowTotal: week.entries.filter(e => e.projectCode === code).reduce((s, e) => s + e.hours, 0),
  }));

  const dailyTotals = days.map(day => {
    const workHours = week.entries
      .filter(e => e.dateWorked === day && e.projectCode !== BANK_HOLIDAY_PROJECT_CODE)
      .reduce((s, e) => s + e.hours, 0);
    // A Mon-Sun week can straddle two calendar months — only count a BH day here if
    // it actually falls within this approval's period month, so a boundary day (e.g.
    // 31 Aug in a "W/C 31 Aug" week belonging to September) isn't double-attributed.
    const bhHours = (bhInWeekMap.has(day) && !daysWithProjectHours.has(day) && day.slice(0, 7) === item.periodMonth)
      ? VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS
      : 0;
    return workHours + bhHours;
  });
  const grandTotal = dailyTotals.reduce((s, t) => s + t, 0);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        style={{
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14,
          width: '100%', maxWidth: 860, maxHeight: '80vh', overflow: 'auto',
          boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
        }}
      >
        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '18px 20px', borderBottom: '1px solid var(--border)',
            position: 'sticky', top: 0, background: 'var(--surface)',
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              {item.personName} — {week.weekNumber} Detail
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{week.label}</div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', fontSize: 20, lineHeight: 1 }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: 20, overflowX: 'auto' }}>
          <table className="ts-tbl">
            <thead>
              <tr>
                <th className="tcol">Project / Task</th>
                {DAY_NAMES.map((d, i) => {
                  const isBH = bhInWeekMap.has(days[i]);
                  return (
                    <th key={d} style={isBH ? { color: 'var(--amber, #d97706)' } : undefined}>
                      {d}
                      {isBH && (
                        <span
                          title={bhInWeekMap.get(days[i])!.holidayName}
                          style={{
                            display: 'inline-block',
                            width: 5, height: 5,
                            borderRadius: '50%',
                            background: 'var(--amber, #d97706)',
                            marginLeft: 3,
                            verticalAlign: 'middle',
                            marginBottom: 1,
                          }}
                        />
                      )}
                      <br />
                      <small style={{ fontWeight: 400, letterSpacing: 0, textTransform: 'none' }}>
                        {formatShortDate(days[i])}
                      </small>
                    </th>
                  );
                })}
                <th className="ttotal">Total</th>
              </tr>
            </thead>
            <tbody>
              {hoursGrid.map(row => (
                <tr key={row.projectCode}>
                  <td className="tcell">
                    <div className="task-name">{row.projectCode}</div>
                  </td>
                  {row.dayHours.map((h, i) => (
                    <td key={i}>
                      <input
                        className="hr-input-ro"
                        readOnly
                        value={h > 0 ? h : ''}
                        placeholder="–"
                        type="number"
                      />
                    </td>
                  ))}
                  <td className="total-cell">
                    {row.rowTotal > 0 ? fmtHours(row.rowTotal) : '–'}
                  </td>
                </tr>
              ))}
              {bhDaysInWeek.length > 0 && (
                <tr style={{ background: 'rgba(217,119,6,0.05)' }}>
                  <td className="tcell">
                    <div className="task-name" style={{ color: 'var(--amber, #d97706)', fontWeight: 600 }}>
                      🏖 Bank Holiday
                    </div>
                  </td>
                  {days.map((day, i) => {
                    const bh = bhInWeekMap.get(day);
                    if (!bh) {
                      return (
                        <td key={i} style={{ textAlign: 'center', color: 'var(--text-dim)', fontSize: 12 }}>–</td>
                      );
                    }
                    const worked = daysWithProjectHours.has(day);
                    return (
                      <td key={i} style={{ textAlign: 'center' }}>
                        <span
                          title={bh.holidayName}
                          style={worked
                            ? { color: 'var(--amber, #d97706)', textDecoration: 'line-through', opacity: 0.65, fontSize: 12 }
                            : { color: 'var(--amber, #d97706)', fontWeight: 700, fontSize: 12 }
                          }
                        >
                          {VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS}
                        </span>
                      </td>
                    );
                  })}
                  <td className="total-cell" style={{ color: 'var(--amber, #d97706)' }}>
                    {(() => {
                      const countedDays = bhDaysInWeek.filter(d => !daysWithProjectHours.has(d)).length;
                      return countedDays > 0 ? fmtHours(countedDays * VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS) : '–';
                    })()}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="tfoot-row">
                <td className="tfoot-lbl">Daily Total</td>
                {dailyTotals.map((t, i) => (
                  <td key={i} className="tfoot-day">{t > 0 ? fmtHours(t) : '–'}</td>
                ))}
                <td className="tfoot-grand">{fmtHours(grandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── ConfirmBackdrop ──────────────────────────────────────────────────────────

function ConfirmBackdrop({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(10,12,20,0.6)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        animation: 'fadeIn 0.15s ease',
      }}
    >
      {children}
    </div>
  );
}

// ─── RejectModal ──────────────────────────────────────────────────────────────

function RejectModal({
  item,
  note,
  error,
  isActioning,
  onNoteChange,
  onConfirm,
  onClose,
}: {
  item: SuperApprovalItem;
  note: string;
  error: string;
  isActioning: boolean;
  onNoteChange: (v: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <ConfirmBackdrop>
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          width: '100%', maxWidth: 460,
          boxShadow: '0 24px 64px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          animation: 'slideUp 0.2s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Coloured top bar */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, #dc2626, #ef4444)' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '20px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(220,38,38,0.10)',
              border: '1.5px solid rgba(220,38,38,0.22)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#dc2626" style={{ width: 18, height: 18, strokeWidth: 2.5 }}>
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>
                Reject Month
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                This action will notify the employee
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-dim)', fontSize: 18, lineHeight: 1,
              padding: '2px 4px', borderRadius: 4,
              transition: 'color 0.12s',
            }}
            onMouseOver={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)'; }}
            onMouseOut={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-dim)'; }}
          >
            ✕
          </button>
        </div>

        {/* Person info pill */}
        <div style={{ margin: '16px 20px 0' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 10, padding: '10px 14px',
          }}>
            <div className="av" style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>
              {getInitials(item.personName)}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{item.personName}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>
                {item.department}&nbsp;·&nbsp;
                <span style={{ color: 'var(--text)', fontWeight: 500 }}>{formatPeriodMonthLabel(item.periodMonth)}</span>
                &nbsp;·&nbsp;{fmtHours(item.weeks.reduce((s, w) => s + w.totalHours + w.bhHours, 0))} total
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
              Rejection reason&nbsp;<span style={{ color: 'var(--red)', fontWeight: 700 }}>*</span>
              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>(min. 10 characters)</span>
            </label>
            <span style={{ fontSize: 11, color: note.trim().length >= 10 ? 'var(--text-muted)' : 'var(--red)', flexShrink: 0 }}>
              {note.trim().length}/10
            </span>
          </div>
          <textarea
            className="fnote"
            value={note}
            onChange={e => onNoteChange(e.target.value)}
            placeholder="Explain why this timesheet is being rejected…"
            style={{ resize: 'vertical', minHeight: 90 }}
          />
          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 12, color: 'var(--red)', marginTop: 6,
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2.5, flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><circle cx="12" cy="16" r="0.5" fill="currentColor" />
              </svg>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          padding: '16px 20px 20px',
          borderTop: '1px solid var(--border)',
          marginTop: 16,
        }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={isActioning}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={isActioning || note.trim().length < 10}
            style={{ minWidth: 140 }}
          >
            {isActioning ? (
              <><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />&nbsp;Rejecting…</>
            ) : (
              <><XIcon />&nbsp;Confirm Rejection</>
            )}
          </button>
        </div>
      </div>
    </ConfirmBackdrop>
  );
}

// ─── ApproveModal ─────────────────────────────────────────────────────────────

function ApproveModal({
  item,
  isActioning,
  onConfirm,
  onClose,
}: {
  item: SuperApprovalItem;
  isActioning: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <ConfirmBackdrop>
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          width: '100%', maxWidth: 420,
          boxShadow: '0 24px 64px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          animation: 'slideUp 0.2s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Coloured top bar */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, #16a34a, #22c55e)' }} />

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '20px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(22,163,74,0.10)',
              border: '1.5px solid rgba(22,163,74,0.22)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" style={{ width: 18, height: 18, strokeWidth: 2.5 }}>
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>
                Final Approve Month
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                Timesheet will be locked after approval
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-dim)', fontSize: 18, lineHeight: 1,
              padding: '2px 4px', borderRadius: 4,
              transition: 'color 0.12s',
            }}
            onMouseOver={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)'; }}
            onMouseOut={e => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-dim)'; }}
          >
            ✕
          </button>
        </div>

        {/* Person info pill */}
        <div style={{ margin: '16px 20px 0' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--surface2)',
            border: '1px solid var(--border)',
            borderRadius: 10, padding: '10px 14px',
          }}>
            <div className="av" style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>
              {getInitials(item.personName)}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{item.personName}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>
                {item.department}&nbsp;·&nbsp;
                <span style={{ color: 'var(--text)', fontWeight: 500 }}>{formatPeriodMonthLabel(item.periodMonth)}</span>
                &nbsp;·&nbsp;{fmtHours(item.weeks.reduce((s, w) => s + w.totalHours + w.bhHours, 0))} total
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation message */}
        <div style={{ padding: '14px 20px 0' }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, margin: 0 }}>
            You&apos;re about to give final approval for{' '}
            <strong style={{ color: 'var(--text)' }}>{formatPeriodMonthLabel(item.periodMonth)}</strong> for{' '}
            <strong style={{ color: 'var(--text)' }}>{item.personName}</strong>.
            This will lock the timesheet permanently.
          </p>
        </div>

        {/* Hours summary chips */}
        <div style={{ display: 'flex', gap: 8, padding: '12px 20px 0', flexWrap: 'wrap' }}>
          {[
            { label: 'Total hours', value: fmtHours(item.weeks.reduce((s, w) => s + w.totalHours + w.bhHours, 0)), color: 'var(--accent)' },
            { label: 'Weeks', value: `${item.weeks.length}`, color: 'var(--green)' },
          ].map(chip => (
            <div key={chip.label} style={{
              flex: 1, minWidth: 100,
              background: 'var(--surface2)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '8px 12px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: chip.color }}>{chip.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{chip.label}</div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', gap: 8, justifyContent: 'flex-end',
          padding: '16px 20px 20px',
          borderTop: '1px solid var(--border)',
          marginTop: 16,
        }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={isActioning}>
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={onConfirm}
            disabled={isActioning}
            style={{ minWidth: 140 }}
          >
            {isActioning ? (
              <><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />&nbsp;Approving…</>
            ) : (
              <><StarIcon />&nbsp;Yes, Final Approve</>
            )}
          </button>
        </div>
      </div>
    </ConfirmBackdrop>
  );
}

// ─── UnlockModal ──────────────────────────────────────────────────────────────

function UnlockModal({
  item,
  note,
  error,
  isActioning,
  onNoteChange,
  onConfirm,
  onClose,
}: {
  item: SuperApprovalItem;
  note: string;
  error: string;
  isActioning: boolean;
  onNoteChange: (v: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [step, setStep] = React.useState<1 | 2>(1);

  const handleClose = () => { setStep(1); onClose(); };

  return (
    <ConfirmBackdrop>
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          width: '100%', maxWidth: 460,
          boxShadow: '0 24px 64px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          animation: 'slideUp 0.2s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        <div style={{ height: 4, background: 'linear-gradient(90deg, #d97706, #f59e0b)' }} />

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '20px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(217,119,6,0.10)',
              border: '1.5px solid rgba(217,119,6,0.22)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#d97706" style={{ width: 18, height: 18, strokeWidth: 2.5 }}>
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 9.9-1" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>
                Unlock Timesheet
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                {step === 1 ? 'This will reset the timesheet to Draft for editing' : 'Step 2 of 2 — Confirm unlock'}
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', fontSize: 18, lineHeight: 1, padding: '2px 4px', borderRadius: 4 }}
          >
            ✕
          </button>
        </div>

        {/* Person info pill */}
        <div style={{ margin: '16px 20px 0' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--surface2)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '10px 14px',
          }}>
            <div className="av" style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>
              {item.personName.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{item.personName}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 1 }}>
                {item.department}&nbsp;·&nbsp;
                <span style={{ color: 'var(--text)', fontWeight: 500 }}>
                  {formatPeriodMonthLabel(item.periodMonth)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {step === 1 ? (
          <>
            <div style={{ padding: '16px 20px 0' }}>
              <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 6 }}>
                Reason for unlock&nbsp;<span style={{ color: 'var(--red)', fontWeight: 700 }}>*</span>
              </label>
              <textarea
                className="fnote"
                value={note}
                onChange={e => onNoteChange(e.target.value)}
                placeholder="Explain why this timesheet is being unlocked…"
                style={{ resize: 'vertical', minHeight: 90 }}
              />
            </div>
            <div style={{
              display: 'flex', gap: 8, justifyContent: 'flex-end',
              padding: '16px 20px 20px',
              borderTop: '1px solid var(--border)',
              marginTop: 16,
            }}>
              <button className="btn btn-ghost" onClick={handleClose}>Cancel</button>
              <button
                className="btn"
                onClick={() => setStep(2)}
                disabled={!note.trim()}
                style={{ minWidth: 110, background: '#d97706', color: '#fff', border: 'none' }}
              >
                Next →
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ padding: '16px 20px 0' }}>
              <div style={{
                background: 'rgba(217,119,6,0.08)',
                border: '1px solid rgba(217,119,6,0.28)',
                borderRadius: 10, padding: '14px 16px',
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 6 }}>
                  Are you sure you want to unlock this timesheet?
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
                  This will remove all approval dates and return the timesheet to <strong>Draft</strong>,
                  allowing the employee to make changes and resubmit.
                </div>
                <div style={{
                  marginTop: 10, paddingTop: 10,
                  borderTop: '1px solid rgba(217,119,6,0.2)',
                  fontSize: 12, color: 'var(--text-muted)',
                }}>
                  Reason:&nbsp;<em style={{ color: 'var(--text)' }}>{note}</em>
                </div>
              </div>
              {error && (
                <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 8 }}>{error}</div>
              )}
            </div>
            <div style={{
              display: 'flex', gap: 8, justifyContent: 'flex-end',
              padding: '16px 20px 20px',
              borderTop: '1px solid var(--border)',
              marginTop: 16,
            }}>
              <button className="btn btn-ghost" onClick={() => setStep(1)} disabled={isActioning}>
                ← Back
              </button>
              <button className="btn btn-ghost" onClick={handleClose} disabled={isActioning}>
                No, Cancel
              </button>
              <button
                className="btn"
                onClick={onConfirm}
                disabled={isActioning}
                style={{ minWidth: 130, background: '#d97706', color: '#fff', border: 'none' }}
              >
                {isActioning ? (
                  <><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />&nbsp;Unlocking…</>
                ) : (
                  <><UnlockIcon />&nbsp;Yes, Unlock</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </ConfirmBackdrop>
  );
}

// ─── SuperApproverDashboard ───────────────────────────────────────────────────

export interface SuperApproverDashboardProps {
  approverEmail: string;
  approverName: string;
  softLaunchMode?: boolean;
  approvals: UseSuperApprovalsResult;
}

export function SuperApproverDashboard({ approverEmail, approverName, softLaunchMode, approvals }: SuperApproverDashboardProps) {
  const [filter, setFilter] = useState<'pending' | 'done'>('pending');
  const [drillDown, setDrillDown] = useState<{ item: SuperApprovalItem; week: WeekSummary } | null>(null);
  const [approveTarget, setApproveTarget] = useState<SuperApprovalItem | null>(null);
  const [rejectTarget, setRejectTarget] = useState<SuperApprovalItem | null>(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectError, setRejectError] = useState('');
  const [unlockTarget, setUnlockTarget] = useState<SuperApprovalItem | null>(null);
  const [unlockNote, setUnlockNote] = useState('');
  const [unlockError, setUnlockError] = useState('');

  const {
    pendingItems,
    reviewedItems,
    isLoading,
    isReviewedLoading,
    loadError,
    isActioning,
    actionError,
    superApproveMonth,
    rejectMonth,
    unlockMonth,
    loadReviewed,
  } = approvals;

  const incomplete = useIncompleteTimesheets();
  const chases = useChaseNotifications();

  const handleFilterChange = (f: 'pending' | 'done') => {
    setFilter(f);
    if (f === 'done') void loadReviewed();
  };

  const openApprove = (item: SuperApprovalItem) => {
    if (item.status !== TimesheetStatus.ManagerApproved
        && !(softLaunchMode && item.status === TimesheetStatus.Submitted)) return;
    setApproveTarget(item);
  };

  const handleApproveConfirm = async () => {
    if (!approveTarget) return;
    try {
      await superApproveMonth(approveTarget, approverName, approverEmail);
      setApproveTarget(null);
    } catch {
      setApproveTarget(null);
    }
  };

  const openReject = (item: SuperApprovalItem) => {
    setRejectTarget(item);
    setRejectNote('');
    setRejectError('');
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;
    setRejectError('');
    try {
      await rejectMonth(rejectTarget, rejectNote, approverName, approverEmail);
      setRejectTarget(null);
      setRejectNote('');
    } catch (e) {
      setRejectError(e instanceof Error ? e.message : 'Rejection failed.');
    }
  };

  const openUnlock = (item: SuperApprovalItem) => {
    setUnlockTarget(item);
    setUnlockNote('');
    setUnlockError('');
  };

  const handleUnlockConfirm = async () => {
    if (!unlockTarget) return;
    setUnlockError('');
    try {
      await unlockMonth(unlockTarget, unlockNote, approverName, approverEmail);
      setUnlockTarget(null);
      setUnlockNote('');
    } catch (e) {
      setUnlockError(e instanceof Error ? e.message : 'Unlock failed.');
    }
  };

  const displayItems = filter === 'pending' ? pendingItems : reviewedItems;
  const isDisplayLoading = filter === 'pending' ? isLoading : isReviewedLoading;

  const employeeItems = displayItems.filter(a => a.source === 'employee');
  const assetItems = displayItems.filter(a => a.source === 'asset');

  const renderCard = (card: SuperApprovalItem) => (
    <SuperApprovalCard
      key={`${card.source}::${card.personId}::${card.periodMonth}::${card.assetCode}`}
      item={card}
      isActioning={isActioning}
      softLaunchMode={softLaunchMode}
      onApprove={() => openApprove(card)}
      onReject={() => openReject(card)}
      onUnlock={() => openUnlock(card)}
      onDrillDown={week => setDrillDown({ item: card, week })}
    />
  );

  const SectionHeader = ({ title, count }: { title: string; count: number }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '4px 2px 10px', paddingTop: 4 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', letterSpacing: '0.02em' }}>
        {title}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
        {count} {count === 1 ? 'item' : 'items'}
      </div>
    </div>
  );

  return (
    <div id="view-super" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Topbar */}
      <div className="topbar">
        <div className="topbar-l">
          <div className="crumb">
            <span>Timesheets</span>
            <span className="sep">/</span>
            <span className="cur">Super Approver</span>
          </div>
        </div>
        <div className="topbar-r">
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 12 }}>
            {pendingItems.length > 0 && `${pendingItems.length} pending final approval`}
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              className={`filter-tab${filter === 'pending' ? ' active' : ''}`}
              onClick={() => handleFilterChange('pending')}
            >
              Needs Action&nbsp;<span>({pendingItems.length})</span>
            </button>
            <button
              className={`filter-tab${filter === 'done' ? ' active' : ''}`}
              onClick={() => handleFilterChange('done')}
            >
              Reviewed
            </button>
          </div>
        </div>
      </div>

      {/* Cards area */}
      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg)', padding: '20px 28px' }}>
        {filter === 'pending' && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              Pending Final Approvals
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
              All manager-approved timesheets awaiting your final sign-off
            </div>
          </div>
        )}

        {loadError && (
          <div className="err-banner" style={{ marginBottom: 16 }}>{loadError.message}</div>
        )}
        {actionError && (
          <div className="err-banner" style={{ marginBottom: 16 }}>{actionError.message}</div>
        )}

        {isDisplayLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 48 }}>
            <div className="spinner" aria-label="Loading" />
          </div>
        ) : displayItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-dim)', fontSize: 13 }}>
            {filter === 'pending'
              ? 'No timesheets awaiting final approval'
              : 'No reviewed timesheets'}
          </div>
        ) : (
          <>
            {employeeItems.length > 0 && (
              <>
                <SectionHeader title="Employee Timesheets" count={employeeItems.length} />
                {employeeItems.map(renderCard)}
              </>
            )}
            {assetItems.length > 0 && (
              <>
                <div style={{ marginTop: employeeItems.length > 0 ? 8 : 0 }}>
                  <SectionHeader title="Asset Timesheets" count={assetItems.length} />
                </div>
                {assetItems.map(renderCard)}
              </>
            )}
          </>
        )}

        {filter === 'pending' && (
          <IncompleteTimesheetsSection
            incomplete={incomplete}
            chases={chases}
          />
        )}
      </div>

      {/* Approve confirmation modal */}
      {approveTarget && (
        <ApproveModal
          item={approveTarget}
          isActioning={isActioning}
          onConfirm={() => void handleApproveConfirm()}
          onClose={() => setApproveTarget(null)}
        />
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <RejectModal
          item={rejectTarget}
          note={rejectNote}
          error={rejectError}
          isActioning={isActioning}
          onNoteChange={setRejectNote}
          onConfirm={() => void handleRejectConfirm()}
          onClose={() => setRejectTarget(null)}
        />
      )}

      {/* Week drill-down modal */}
      {drillDown && (
        <WeekDrillDownModal
          item={drillDown.item}
          week={drillDown.week}
          onClose={() => setDrillDown(null)}
        />
      )}

      {/* Unlock modal */}
      {unlockTarget && (
        <UnlockModal
          item={unlockTarget}
          note={unlockNote}
          error={unlockError}
          isActioning={isActioning}
          onNoteChange={setUnlockNote}
          onConfirm={() => void handleUnlockConfirm()}
          onClose={() => setUnlockTarget(null)}
        />
      )}
    </div>
  );
}
